import { useState } from "react";
import { Check, ChevronsUpDown, Languages } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";

export const LANGUAGES = [
  { value: "auto", label: "Auto-Detect", native: "Match client language", flag: "🎯" },
  // English variants block
  { value: "en-US", label: "American English", native: "US English", flag: "🇺🇸" },
  { value: "en-GB", label: "British English", native: "UK English", flag: "🇬🇧" },
  { value: "en-AU", label: "Australian English", native: "Aussie business", flag: "🇦🇺" },
  // Alphabetical
  { value: "ar", label: "Arabic", native: "العربية", flag: "🇸🇦" },
  { value: "de-AT", label: "Austrian German", native: "Österreichisches Deutsch", flag: "🇦🇹" },
  { value: "zh", label: "Chinese (Mandarin)", native: "简体中文", flag: "🇨🇳" },
  { value: "cs", label: "Czech", native: "Čeština", flag: "🇨🇿" },
  { value: "da", label: "Danish", native: "Dansk", flag: "🇩🇰" },
  { value: "nl", label: "Dutch", native: "Nederlands", flag: "🇳🇱" },
  { value: "fi", label: "Finnish", native: "Suomi", flag: "🇫🇮" },
  { value: "fr", label: "French", native: "Français", flag: "🇫🇷" },
  { value: "de", label: "German", native: "Deutsch", flag: "🇩🇪" },
  { value: "el", label: "Greek", native: "Ελληνικά", flag: "🇬🇷" },
  { value: "hi", label: "Hindi", native: "हिन्दी", flag: "🇮🇳" },
  { value: "hu", label: "Hungarian", native: "Magyar", flag: "🇭🇺" },
  { value: "is", label: "Icelandic", native: "Íslenska", flag: "🇮🇸" },
  { value: "it", label: "Italian", native: "Italiano", flag: "🇮🇹" },
  { value: "ja", label: "Japanese", native: "日本語 (敬語)", flag: "🇯🇵" },
  { value: "ko", label: "Korean", native: "한국어", flag: "🇰🇷" },
  { value: "pt", label: "Portuguese", native: "Português", flag: "🇵🇹" },
  { value: "ru", label: "Russian", native: "Русский", flag: "🇷🇺" },
  { value: "sk", label: "Slovak", native: "Slovenčina", flag: "🇸🇰" },
  { value: "es", label: "Spanish (Universal / Mexican)", native: "Español", flag: "🇪🇸" },
  { value: "sv", label: "Swedish", native: "Svenska", flag: "🇸🇪" },
];

export default function LanguagePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = LANGUAGES.find((l) => l.value === value) || LANGUAGES[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="language-picker-trigger"
          aria-expanded={open}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-[#1E1E28] bg-[#0B0B0E] px-3 text-sm text-white outline-none transition hover:border-[#FF2E4D]/50 focus:border-[#FF2E4D] focus:ring-1 focus:ring-[#FF2E4D]/50"
        >
          <span className="flex items-center gap-2 truncate">
            <span className="text-base leading-none">{selected.flag}</span>
            <span className="truncate">{selected.label}</span>
            {selected.value !== "auto" && (
              <span className="hidden sm:inline text-zinc-500 text-xs truncate">
                · {selected.native}
              </span>
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-zinc-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 border-[#1E1E28] bg-[#0F0F13] text-white"
        align="start"
      >
        <Command className="bg-[#0F0F13] text-white">
          <div className="flex items-center gap-2 border-b border-[#1E1E28] px-3">
            <Languages className="h-4 w-4 text-[#FF2E4D]" />
            <CommandInput
              data-testid="language-picker-search"
              placeholder="Search language..."
              className="h-11 bg-transparent text-white placeholder:text-zinc-600"
            />
          </div>
          <CommandList className="rb-scroll max-h-64">
            <CommandEmpty className="py-4 text-center text-sm text-zinc-500">
              No language found.
            </CommandEmpty>
            <CommandGroup>
              {LANGUAGES.map((lang) => (
                <CommandItem
                  key={lang.value}
                  data-testid={`language-option-${lang.value}`}
                  value={`${lang.label} ${lang.native} ${lang.value}`}
                  onSelect={() => {
                    onChange(lang.value);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-3 text-white aria-selected:bg-[#15151C] data-[selected=true]:bg-[#15151C]"
                >
                  <span className="text-lg leading-none">{lang.flag}</span>
                  <div className="flex flex-1 flex-col leading-tight">
                    <span className="text-sm font-medium">{lang.label}</span>
                    <span className="text-[11px] text-zinc-500">
                      {lang.native}
                    </span>
                  </div>
                  {value === lang.value && (
                    <Check className="h-4 w-4 text-[#FF2E4D]" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
